import React from 'react';
import ElementBuilderConfig from '../ElementBuilder/element.config';
import ScreenBuilderConfig from '../ScreenBuilder/screen.config';

export const VIEWPORT_CATEGORIES = {
	mobile: 'mobile',
	tablet: 'tablet',
	desktop: 'desktop',
};

export const VIEW_TYPES = {
	list: 'list',
	grid: 'grid',
	table: 'table',
};

export const DISPLAY_PREFERENCE_VERSION = 1;
export const DISPLAY_PREFERENCE_NAMESPACE = 'synapse:display-preference';

const IDEAL_VIEW_BY_VIEWPORT = {
	[VIEWPORT_CATEGORIES.mobile]: VIEW_TYPES.list,
	[VIEWPORT_CATEGORIES.tablet]: VIEW_TYPES.grid,
	[VIEWPORT_CATEGORIES.desktop]: VIEW_TYPES.table,
};

const SCREEN_VIEW_TYPES = {
	standardListV1: VIEW_TYPES.list,
	standardGridV1: VIEW_TYPES.grid,
	standardTableV1: VIEW_TYPES.table,
};

export const getViewportCategory = width => {
	if (width <= 767) return VIEWPORT_CATEGORIES.mobile;
	if (width <= 1024) return VIEWPORT_CATEGORIES.tablet;
	return VIEWPORT_CATEGORIES.desktop;
};

export const getIdealViewType = viewportCategory =>
	IDEAL_VIEW_BY_VIEWPORT[viewportCategory] || VIEW_TYPES.table;

/**
 * Fit Screens expose their renderer through `key`. Known keys are mapped
 * explicitly; the suffix fallback keeps custom list/grid/table screens eligible.
 */
export const classifyScreenView = screen => {
	const key = screen?.key;
	if (!key || typeof key !== 'string') return undefined;
	if (SCREEN_VIEW_TYPES[key]) return SCREEN_VIEW_TYPES[key];
	// Registered as a key/icon in legacy config, but it has no screen renderer.
	if (key === 'invReqTableV1') return undefined;

	const normalizedKey = key.toLowerCase();
	const conventionalType = normalizedKey.match(/(table|grid|list)v\d+$/)?.[1];
	if (conventionalType) return conventionalType;
	return undefined;
};

const findPersistedScreen = (screens, persistedSelection) => {
	if (!persistedSelection) return undefined;
	return screens.find(screen => screen.id === persistedSelection.screenId) ||
		screens.find(screen => screen.key === persistedSelection.screenKey);
};

export const isScreenRenderable = screen => {
	if (!ScreenBuilderConfig.getAuthorizedScreenReactNode(screen?.key)) return false;
	const viewType = classifyScreenView(screen);
	const isElementDriven =
		viewType === VIEW_TYPES.list || viewType === VIEW_TYPES.grid;
	return (
		!isElementDriven ||
		ElementBuilderConfig.getAuthorizedElements(screen?.elements).some(element =>
			ElementBuilderConfig.getAuthorizedElementReactNode(element.key),
		)
	);
};

const resolveElement = (screen, persistedSelection) => {
	const elements = ElementBuilderConfig.getAuthorizedElements(screen?.elements);
	const persistedElement = elements.find(
		element => element.id === persistedSelection?.elementId,
	) || elements.find(element => element.key === persistedSelection?.elementKey);

	return persistedElement || elements[0];
};

const toResolvedSelection = (screen, persistedSelection, explicit = false) => {
	if (!screen) return {};
	const element = resolveElement(screen, persistedSelection);
	return {
		screenId: screen.id,
		screenKey: screen.key,
		elementId: element?.id,
		elementKey: element?.key,
		explicit,
	};
};

/**
 * Resolves a configured Fit Screen/Fit Element pair. An explicit selection for
 * the current viewport wins while it remains authorized. Otherwise the ideal
 * semantic view is used, followed by a compatible persisted fallback and the
 * legacy first-screen/first-element behavior.
 */
export const resolveDisplaySelection = ({
	screens = [],
	viewportCategory,
	persistedSelection,
}) => {
	if (!screens.length) return {};

	const renderableScreens = screens.filter(isScreenRenderable);
	const persistedScreen = findPersistedScreen(
		renderableScreens,
		persistedSelection,
	);
	if (persistedSelection?.explicit && persistedScreen)
		return toResolvedSelection(persistedScreen, persistedSelection, true);

	const idealViewType = getIdealViewType(viewportCategory);
	const idealScreen = renderableScreens.find(
		screen => classifyScreenView(screen) === idealViewType,
	);

	if (persistedScreen && classifyScreenView(persistedScreen) === idealViewType)
		return toResolvedSelection(persistedScreen, persistedSelection);
	if (idealScreen) return toResolvedSelection(idealScreen);
	if (persistedScreen)
		return toResolvedSelection(persistedScreen, persistedSelection);
	if (renderableScreens.length) return toResolvedSelection(renderableScreens[0]);
	return toResolvedSelection(screens[0]);
};

export const createDisplayPreferenceKey = ({ userId, contextId }) => {
	if (userId === undefined || userId === null || !contextId) return undefined;
	return `${DISPLAY_PREFERENCE_NAMESPACE}:v${DISPLAY_PREFERENCE_VERSION}:${encodeURIComponent(
		String(userId),
	)}:${encodeURIComponent(String(contextId))}`;
};

export const readDisplayPreferences = ({ userId, contextId, storage }) => {
	const key = createDisplayPreferenceKey({ userId, contextId });
	if (!key || !storage) return {};

	try {
		const parsed = JSON.parse(storage.getItem(key));
		if (
			parsed?.version !== DISPLAY_PREFERENCE_VERSION ||
			typeof parsed.selections !== 'object' ||
			!parsed.selections
		)
			return {};
		return parsed.selections;
	} catch (_error) {
		return {};
	}
};

export const writeDisplayPreference = ({
	userId,
	contextId,
	viewportCategory,
	selection,
	storage,
}) => {
	const key = createDisplayPreferenceKey({ userId, contextId });
	if (
		!key ||
		!storage ||
		!viewportCategory ||
		selection?.screenId === undefined ||
		selection?.screenId === null
	)
		return;

	const selections = readDisplayPreferences({ userId, contextId, storage });
	try {
		storage.setItem(
			key,
			JSON.stringify({
				version: DISPLAY_PREFERENCE_VERSION,
				selections: {
					...selections,
					[viewportCategory]: selection,
				},
			}),
		);
	} catch (_error) {
		// Storage can be unavailable or full. Display resolution must still work.
	}
};

export const getBrowserStorage = () => {
	try {
		return window.localStorage;
	} catch (_error) {
		return undefined;
	}
};

export const useViewportCategory = () => {
	const getCategory = () =>
		getViewportCategory(typeof window === 'undefined' ? 1025 : window.innerWidth);
	const [category, setCategory] = React.useState(getCategory);

	React.useEffect(() => {
		if (typeof window === 'undefined') return undefined;
		const onResize = () => {
			const nextCategory = getCategory();
			setCategory(current => (current === nextCategory ? current : nextCategory));
		};
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	return category;
};
